import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { safeTrim } from "../utils/security";

const getFriendlyAuthError = (authError, fallbackMessage) => {
  const rawMessage = authError?.message || "";
  const rawCode = authError?.code || authError?.status || "";
  const normalizedError = `${rawMessage} ${rawCode}`.toLowerCase();

  if (normalizedError.includes("email rate limit exceeded")) {
    return "Too many password reset requests. Please wait and try again.";
  }

  if (normalizedError.includes("invalid login credentials")) {
    return "Incorrect email or password.";
  }

  if (normalizedError.includes("email not confirmed")) {
    return "Please verify your email before logging in.";
  }

  return fallbackMessage;
};

function AdminLoginPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [sendingReset, setSendingReset] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    setError("");
    setResetMessage("");

    const failedAttemptsKey = "adminFailedAttempts";
    const lockUntilKey = "adminLockUntil";
    const lockoutMessage = "Too many failed attempts. Please wait 5 minutes.";
    const maxFailedAttempts = 5;
    const lockoutDuration = 5 * 60 * 1000;
    const now = Date.now();
    const failedAttempts = Number(localStorage.getItem(failedAttemptsKey)) || 0;
    const lockUntil = Number(localStorage.getItem(lockUntilKey)) || 0;

    // Lockout logic: stop login attempts while the saved lockout timestamp is active.
    if (lockUntil && now < lockUntil) {
      setError(lockoutMessage);
      return;
    }

    const loginEmail = safeTrim(email);

    const { error } = await supabase.auth.signInWithPassword({
      email: loginEmail,
      password,
    });

    if (error) {
      console.error("Supabase admin login error:", error);

      // Failed attempts tracking: count failed Supabase logins in localStorage.
      const updatedFailedAttempts = failedAttempts + 1;
      localStorage.setItem(failedAttemptsKey, updatedFailedAttempts.toString());

      if (updatedFailedAttempts >= maxFailedAttempts) {
        localStorage.setItem(lockUntilKey, (now + lockoutDuration).toString());
        setError(lockoutMessage);
        return;
      }

      setError(getFriendlyAuthError(error, "Unable to log in. Please try again."));
      return;
    }

    // Successful reset logic: clear lockout state after a valid admin login.
    localStorage.removeItem(failedAttemptsKey);
    localStorage.removeItem(lockUntilKey);

    navigate("/admin");
  };

  const handlePasswordReset = async () => {
    setError("");
    setResetMessage("");

    const resetEmail = safeTrim(email);

    if (!resetEmail) {
      setError("Enter your admin email before requesting a password reset.");
      return;
    }

    setSendingReset(true);

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/update-password`,
    });

    setSendingReset(false);

    if (resetError) {
      console.error("Supabase password reset error:", resetError);
      setError(getFriendlyAuthError(resetError, "Unable to send password reset email. Please try again."));
      return;
    }

    setResetMessage("Password reset email sent. Check your inbox.");
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-black px-6 text-white">
      <Helmet>
        <title>Admin Login | Limassol Airport Taxi</title>
        <meta
          name="description"
          content="Secure admin login for Limassol Airport Taxi booking management."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.04] p-8">
        <h1 className="text-3xl font-bold text-yellow-400">
          Admin Login
        </h1>

        <p className="mt-3 text-neutral-400">
          Login to manage taxi bookings.
        </p>

        <form onSubmit={handleLogin} className="mt-8 space-y-4" autoComplete="off">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-2xl border border-white/10 bg-neutral-900 px-4 py-4 text-white outline-none focus:border-yellow-400"
            placeholder="Email"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
          />

          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-2xl border border-white/10 bg-neutral-900 px-4 py-4 text-white outline-none focus:border-yellow-400"
            placeholder="Password"
          />

          {error && (
            <p className="rounded-xl bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-400">
              {error}
            </p>
          )}

          {resetMessage && (
            <p className="rounded-xl bg-green-500/10 px-4 py-3 text-sm font-semibold text-green-400">
              {resetMessage}
            </p>
          )}

          <button
            type="submit"
            className="w-full rounded-2xl bg-yellow-400 px-6 py-4 font-bold text-black hover:bg-yellow-300"
          >
            Login
          </button>

          <button
            type="button"
            onClick={handlePasswordReset}
            disabled={sendingReset}
            className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-6 py-4 font-bold text-white transition hover:bg-white/[0.08] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {sendingReset ? "Sending Reset Email..." : "Forgot Password?"}
          </button>
        </form>
      </div>
    </main>
  );
}

export default AdminLoginPage;
